import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core'
import { ControlValueAccessor, FormBuilder, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator, Validators } from '@angular/forms'
import { AppointmentModel, InsuranceProviderConfigurationModel, ProductModel } from '@mmx/shared'
import moment from 'moment-timezone'
import { map, Subject, takeUntil } from 'rxjs'

export interface ServiceReviewInquiryFormValue {
  fromDate?: moment.Moment
  toDate?: moment.Moment
  certificationIssueDate?: moment.Moment
  certificationNumber?: string
  referenceNumber?: string
}

@Component({
  selector: 'mmx-service-review-inquiry-form',
  templateUrl: './service-review-inquiry-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: ServiceReviewInquiryFormComponent,
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: ServiceReviewInquiryFormComponent,
      multi: true,
    },
  ],
  standalone: false,
})
export class ServiceReviewInquiryFormComponent implements OnInit, OnDestroy, ControlValueAccessor, Validator {
  @Input()
  public type: 'referrals' | 'authorizations'

  @Input()
  public products: ProductModel[] = []

  @Input()
  public configuration: InsuranceProviderConfigurationModel

  @Input()
  public appointment: AppointmentModel

  public formGroup = this.fb.group({
    fromDate: this.fb.control<moment.Moment>(null),
    toDate: this.fb.control<moment.Moment>(null),
    certificationIssueDate: this.fb.control<moment.Moment>(null),
    certificationNumber: this.fb.control<string>(null, [Validators.pattern(/^[a-zA-Z0-9]{1,50}$/), Validators.maxLength(50)]),
    referenceNumber: this.fb.control<string>(null, [Validators.pattern(/^[a-zA-Z0-9]{1,50}$/), Validators.maxLength(50)]),
  })

  private onChange: any = () => {}
  private onTouched: any = () => {}
  private onDestroy$ = new Subject<void>()

  constructor(
    private fb: FormBuilder,
  ) { }

  public ngOnInit() {
    this.formGroup.valueChanges
      .pipe(
        takeUntil(this.onDestroy$),
        map(() => this.getFormattedValue()),
      )
      .subscribe((value) => {
        this.onChange?.(value)
      })
  }

  public ngOnDestroy() {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

  public writeValue(value: Record<string, any>) {
    this.formGroup.patchValue(value)
  }

  public registerOnChange(fn: any): void {
    this.onChange = fn
  }

  public registerOnTouched(fn: any): void {
    this.onTouched = fn
  }

  public validate(): ValidationErrors {
    return this.formGroup.valid ? null : { invalid: true }
  }

  private getFormattedValue() {
    const formValue = this.formGroup.value
    const value: ServiceReviewInquiryFormValue = {
      fromDate: formValue.fromDate,
      toDate: formValue.toDate,
      certificationIssueDate: formValue.certificationIssueDate,
      certificationNumber: formValue.certificationNumber,
      referenceNumber: formValue.referenceNumber,
    }

    return value
  }
}
